import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { 
  type QRCodeConfig, 
  type Restaurant, 
  type QRCodeDownloadOptions 
} from '@/types';
import { 
  generateQRCode,
  createDefaultQRConfig,
  validateQRConfig,
  QR_SIZE_CONFIGS,
  downloadQRCode
} from '@/lib/services/qrCodeService';
import {
  Download,
  RefreshCw,
  Palette,
  Settings,
  Eye,
  Copy
} from 'lucide-react';

interface QRCodeGeneratorProps {
  restaurant: Restaurant;
  onConfigChange?: (config: QRCodeConfig) => void;
}

/**
 * Main QR Code Generator Component
 * Handles QR code generation, customization, and preview
 */
const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({
  restaurant,
  onConfigChange
}) => {
  const [config, setConfig] = useState<QRCodeConfig>(() => 
    createDefaultQRConfig(restaurant.id)
  );
  const [preview, setPreview] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [copyingUrl, setCopyingUrl] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Notify parent of config changes
  useEffect(() => {
    onConfigChange?.(config);
  }, [config, onConfigChange]);

  const generatePreview = useCallback(async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const errors = validateQRConfig(config);
      if (errors.length > 0) {
        setError(errors[0]);
        setPreview(null);
        return;
      }

      const dataUrl = await generateQRCode(config);
      setPreview(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate QR code');
      setPreview(null);
    } finally {
      setGenerating(false);
    }
  }, [config]);

  // Generate preview when config changes
  useEffect(() => {
    generatePreview();
  }, [generatePreview]);

  const handleDownload = async (format: 'png' | 'svg' | 'pdf') => {
    if (!preview) return;
    
    setDownloading(format);
    setError(null);

    try {
      const downloadOptions: QRCodeDownloadOptions = {
        format,
        quality: 'high',
        size: { 
          width: QR_SIZE_CONFIGS[config.size].pixels, 
          height: QR_SIZE_CONFIGS[config.size].pixels 
        }
      };

      await downloadQRCode(config, downloadOptions, restaurant.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  const updateConfig = (updates: Partial<QRCodeConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const resetColors = () => {
    updateConfig({ 
      foregroundColor: '#000000', 
      backgroundColor: '#ffffff' 
    });
  };

  const handleCopyURL = async () => {
    setCopyingUrl(true);
    try {
      await navigator.clipboard.writeText(config.url);
      // You could add a toast notification here if you have one
      console.log('URL copied to clipboard');
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = config.url;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        console.log('URL copied to clipboard (fallback)');
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        setError('Failed to copy URL to clipboard');
      }
      document.body.removeChild(textArea);
    } finally {
      setCopyingUrl(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-red-50 border border-red-200 rounded-lg"
        >
          <p className="text-red-800 text-sm font-medium">{error}</p>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left Column - Configuration */}
        <div className="space-y-6">
          {/* Basic Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              QR Code Settings
            </h3>
            
            <div className="space-y-4">
              {/* Size Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Size
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(QR_SIZE_CONFIGS).map(([size, sizeConfig]) => (
                    <button
                      key={size}
                      onClick={() => updateConfig({ size: size as QRCodeConfig['size'] })}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        config.size === size
                          ? 'border-primary bg-primary bg-opacity-10 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm capitalize">
                        {size.replace('-', ' ')}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {sizeConfig.pixels}×{sizeConfig.pixels}px
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {QR_SIZE_CONFIGS[config.size].description}
                </p>
              </div>

              {/* URL Display */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Menu URL
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyURL}
                    disabled={copyingUrl}
                    loading={copyingUrl}
                    leftIcon={<Copy className="w-4 h-4" />}
                    className="text-xs"
                  >
                    {copyingUrl ? 'Copying...' : 'Copy'}
                  </Button>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-800 font-mono break-all">
                    {config.url}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Color Customization */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Palette className="w-5 h-5 mr-2" />
                Color Customization
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetColors}
                leftIcon={<RefreshCw className="w-4 h-4" />}
                title="Reset to default colors"
              >
                Reset
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Foreground Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={config.foregroundColor}
                    onChange={(e) => updateConfig({ foregroundColor: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                  />
                  <input
                    type="text"
                    value={config.foregroundColor}
                    onChange={(e) => updateConfig({ foregroundColor: e.target.value })}
                    className="flex-1 h-10 text-base font-mono px-3 border border-gray-300 rounded-md min-w-0 
                             bg-white text-gray-900 placeholder-gray-500
                             focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary 
                             transition-[border-color,box-shadow] duration-150"
                    placeholder="#000000"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={config.backgroundColor}
                    onChange={(e) => updateConfig({ backgroundColor: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                  />
                  <input
                    type="text"
                    value={config.backgroundColor}
                    onChange={(e) => updateConfig({ backgroundColor: e.target.value })}
                    className="flex-1 h-10 text-base font-mono px-3 border border-gray-300 rounded-md min-w-0 
                             bg-white text-gray-900 placeholder-gray-500
                             focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary 
                             transition-[border-color,box-shadow] duration-150"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Preview and Download */}
        <div className="space-y-6">
          {/* Preview */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="w-5 h-5 mr-2" />
              Preview
            </h3>
            
            <div className="text-center">
              {generating ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-600">Generating QR code...</p>
                </div>
              ) : preview ? (
                <div className="space-y-4">
                  <div className="inline-block bg-gray-50 rounded-lg">
                    <img 
                      src={preview} 
                      alt="QR Code Preview"
                      className="max-w-full h-auto rounded-lg"
                      style={{ 
                        maxWidth: '256px',
                        maxHeight: '256px'
                      }}
                    />
                  </div>
                  <div className="text-sm text-gray-600">
                    Actual size: {QR_SIZE_CONFIGS[config.size].pixels}×{QR_SIZE_CONFIGS[config.size].pixels}px
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <RefreshCw className="w-8 h-8 mb-4" />
                  <p>Unable to generate preview</p>
                </div>
              )}
            </div>
          </div>

          {/* Download Options */}
          {preview && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Download className="w-5 h-5 mr-2" />
                Download QR Code
              </h3>
              
              <div className="space-y-3">
                {/* PNG Download */}
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => handleDownload('png')}
                  disabled={!!downloading}
                  loading={downloading === 'png'}
                  leftIcon={<Download className="w-4 h-4" />}
                  className="w-full"
                >
                  {downloading === 'png' ? 'Downloading PNG...' : 'Download PNG'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  High-resolution PNG for digital use and basic printing
                </p>

                {/* SVG Download */}
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => handleDownload('svg')}
                  disabled={!!downloading}
                  loading={downloading === 'svg'}
                  leftIcon={<Download className="w-4 h-4" />}
                  className="w-full"
                >
                  {downloading === 'svg' ? 'Downloading SVG...' : 'Download SVG'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Vector format for scalable printing and design work
                </p>

                {/* PDF Download */}
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => handleDownload('pdf')}
                  disabled={!!downloading}
                  loading={downloading === 'pdf'}
                  leftIcon={<Download className="w-4 h-4" />}
                  className="w-full"
                >
                  {downloading === 'pdf' ? 'Downloading PDF...' : 'Download Print-Ready'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Print-ready format with restaurant name and instructions
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
